// src/config/Config.js
// Global configuration for upload feature and OCI object storage.
// Edit the placeholder values when OCI credentials are available.

export const MAX_FILE_SIZE_MB = 10; // Individual file size limit
export const MAX_TOTAL_UPLOAD_SIZE_MB = 30; // Aggregate size within a single upload session
export const ALLOWED_FILE_TYPES = ['.jpg', '.png', '.pdf'];
export const ALLOWED_IMAGE_FILE_TYPES = ['.jpg', '.png'];

// Resumable worksheet upload (scan / PDF)
export const WORKSHEET_SESSION_CREATE = '/worksheet-session/';
export const WORKSHEET_SESSION_ACTIVE = '/worksheet-session/active/';
export const WORKSHEET_SESSION_UPLOAD_PAGE = (id) => `/worksheet-session/${id}/upload-page/`;
export const WORKSHEET_SESSION_CLEAR_PAGES = (id) => `/worksheet-session/${id}/pages/`;
export const WORKSHEET_SESSION_UPLOAD_PDF = (id) => `/worksheet-session/${id}/upload-pdf/`;
export const WORKSHEET_SESSION_FINALIZE = (id) => `/worksheet-session/${id}/finalize/`;
export const WORKSHEET_MAX_PAGES = 40;

// Bulk upload configuration
export const BULK_UPLOAD_MAX_QUESTIONS = 1000; // Maximum questions that can be uploaded at once

// Toggle object storage uploads (set to false to disable uploads entirely)
export const OCI_STORAGE_ENABLED = true;

// ============================================================================
// BASE URL CONFIGURATION
// ============================================================================

const getDefaultBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost:8000';
  }

  const { protocol, hostname, origin } = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  if (isLocalhost) {
    return 'http://localhost:8000';
  }

  return origin || `${protocol}//${hostname}`;
};

export const BASE_URL = (process.env.REACT_APP_API_BASE_URL || getDefaultBaseUrl()).replace(/\/$/, '');
//export const BASE_URL = `https://prepup.thinkstyne.com`;
export const MOBILE_APP_SCHEME = process.env.REACT_APP_MOBILE_APP_SCHEME || 'thinkstyne';

/**
 * Resolve image URL for display (main and sub-question images in edit/view).
 * - Full URL (http/https) or blob/data: return as-is.
 * - Relative path: prepend BASE_URL so img loads from backend.
 */
export function resolveImageDisplayUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  const base = (BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return base + path;
}

// ============================================================================
// THIRD-PARTY INTEGRATIONS
// ============================================================================
export const REACT_APP_GOOGLE_CLIENT_ID = '349924820272-efgimojqqakfrg835i3ojc7dh1t9tfbj.apps.googleusercontent.com';

// Stripe Configuration
export const REACT_APP_STRIPE_PUBLISHABLE_KEY = 'pk_test_dummy';

// ============================================================================
// AUTHENTICATION & USER MANAGEMENT APIs
// ============================================================================
export const AUTH_LOGIN = '/accounts/login/';
export const AUTH_LOGIN_REQUEST_OTP = '/accounts/login/request-otp/';
export const AUTH_LOGIN_VERIFY_OTP = '/accounts/login/verify-otp/';
export const AUTH_LOGOUT = '/accounts/logout/';
export const AUTH_LOGOUT_OTHER_SESSIONS = '/accounts/logout-other-sessions/';
export const AUTH_REGISTER = '/accounts/register/';
export const AUTH_CHANGE_PASSWORD = '/accounts/change-password/';
export const AUTH_PASSWORD_RESET = '/accounts/password-reset/confirm/';
export const AUTH_VERIFY_EMAIL = '/accounts/verify-email/';
export const AUTH_RESEND_VERIFICATION = '/accounts/resend-verification-email/';
export const AUTH_GOOGLE = '/accounts/auth/google/';
export const AUTH_GOOGLE_REGISTER = '/accounts/register/google/';
export const USER_PROFILE = '/accounts/profile/';
export const USER_PROFILE_UPDATE = '/accounts/profile/';
export const CSRF_TOKEN = '/accounts/csrf-token/';
export const CSRF_FALLBACK = '/accounts/csrf/';
export const ACCEPT_INVITE = '/accounts/accept-invite/';
export const CLEAR_USER_SESSIONS = '/accounts/clear-user-sessions/';
export const SESSION_CHECK = '/accounts/session-check/';
export const PARENT_DASHBOARD = '/accounts/parent/parent-dashboard/';
export const TEACHER_DASHBOARD = '/accounts/teacher/teacher-dashboard/';

// ============================================================================
// ACCOUNTS & USER MANAGEMENT APIs
// ============================================================================
export const ACADEMIC_YEARS = '/accounts/academic-years/';
export const ACADEMIC_YEAR_BOARD_SUBJECT_TOPICS = '/accounts/academic-year-board-subject-topics/';
export const SEMESTER_TEMPLATES = '/accounts/semester-templates/';
export const DIFFICULTIES = '/accounts/difficulties/';
export const SUBJECTS = '/accounts/subjects/';
export const BOARDS = '/accounts/boards/';
export const SUBJECT_TOPICS = '/accounts/subject-topics/';
export const STATUSES = '/exams/statuses/';
export const ROLES = '/accounts/roles/';
export const TENANTS = '/accounts/tenants/';
export const FEATURES = '/accounts/features/';
export const FEATURE_ACCESS = '/accounts/feature-access/';

// Student-Teacher Relationships
export const TEACHERS_STUDENTS = '/accounts/teachers/students/';
/** Combined platform relationships + teacher-managed placeholders (teacher dashboard). */
export const TEACHERS_STUDENTS_UNIFIED = `${TEACHERS_STUDENTS}unified-dashboard/`;
/** Teacher-managed student records (pre-onboarding). */
export const TEACHER_MANAGED_STUDENTS = '/accounts/teachers/managed-students/';
/** Teacher student groups (batch orchestration); CRUD under /accounts/teacher/batches/ */
export const TEACHER_BATCHES = '/accounts/teacher/batches/';
export const STUDENT_TEACHERS = '/accounts/student/teachers/';
export const STUDENT_FAMILY = '/accounts/student/family/';
export const STUDENT_EXIT_TEACHER = '/accounts/student/exit-teacher/';

// Student grade & parent consent (first-login safeguard)
export const STUDENT_SET_GRADE = '/accounts/student/set-grade/';
export const STUDENT_SUBMIT_PARENT_EMAIL = '/accounts/student/submit-parent-email/';
export const STUDENT_RESEND_PARENT_CONSENT = '/accounts/student/resend-parent-consent/';
export const STUDENT_PLANS = '/accounts/student/plans/';
export const STUDENT_REQUEST_UPGRADE = '/accounts/student/request-upgrade/';
export const VERIFY_PARENT_CONSENT = '/accounts/verify-parent-consent/';

// Parent: children & upgrade requests
export const PARENT_CHILDREN = '/accounts/parent/children/';
export const PARENT_UPGRADE_REQUESTS = '/accounts/parent/upgrade-requests/';
export const PARENT_CHECKOUT_FOR_UPGRADE = '/accounts/parent/checkout-for-upgrade/';

// Pricing & Subscriptions
export const PRICING_PLANS = '/accounts/pricing-plans/';
export const USER_SUBSCRIPTIONS = '/accounts/user-subscriptions/';
export const SUBSCRIPTION_CHANGE = '/accounts/subscription/change/';
export const SUBSCRIPTION_REACTIVATE = '/accounts/subscription/reactivate/';
export const SUBSCRIPTION_DOWNGRADE = '/accounts/subscription/downgrade/';
export const SUBSCRIPTION_TOGGLE_AUTO_RENEW = '/accounts/subscription/toggle-auto-renew/';
export const SUBSCRIPTION_CANCEL_SCHEDULED_DOWNGRADE = '/accounts/subscription/cancel-scheduled-downgrade/';
export const SUBSCRIPTION_RESOLVE_EXPIRED = '/accounts/subscription/resolve-expired/';
export const PLAN_VALIDATION = '/accounts/plan-validation/';

// Add-ons & Entitlements
export const ADDONS_CATALOG = '/accounts/addons/catalog/';
export const ADDONS_HISTORY = '/accounts/addons/history/';
export const ADDONS_CART = '/accounts/addons/cart/';
export const ADDONS_CART_CLEAR = '/accounts/addons/cart/clear/';
export const ADDONS_PAYMENT_SUCCESS = '/accounts/addons/payment-success/';
export const ENTITLEMENTS = '/accounts/entitlements/';
export const ADDON_CATALOG = '/accounts/addon-catalog/';

// ============================================================================
// REFERRAL & PROMOTIONS APIs
// ============================================================================
export const REFERRAL_MY_CODE = '/referrals/my-code/';
export const REFERRAL_CHECK_INVITE_EMAILS = '/referrals/check-invite-emails/';
export const REFERRAL_CREATE_INVITE = '/referrals/create-invite/';
export const REFERRAL_SEND_TEACHER_INVITE = '/referrals/send-teacher-invite/';
export const REFERRAL_MY_INVITES = '/referrals/my-invites/';
export const REFERRAL_INVITE_SIGNUPS = (id) => `/referrals/invites/${id}/signups/`;
export const REFERRAL_INVITE_STATS = (id) => `/referrals/invites/${id}/stats/`;
export const REFERRAL_UPDATE_MY_INVITE = (id) => `/referrals/invites/${id}/`;
export const REFERRAL_CHAMPIONS = '/referrals/champions/';
export const REFERRAL_CHAMPION_MARGIN = (userId) => `/referrals/champions/${userId}/margin/`;
export const REFERRAL_MY_MARGIN = '/referrals/my-margin/';
export const REFERRAL_MY_REFERRALS = '/referrals/my-referrals/';
export const REFERRAL_MY_TRACKING_STATS = '/referrals/my-tracking-stats/';
export const REFERRAL_MY_INVITE_LOGS = '/referrals/my-invite-logs/';
export const REFERRAL_ADMIN_STATS = '/admin/stats/';
export const REFERRAL_CODES = '/codes/';
export const REFERRAL_REDEMPTIONS = '/redemptions/';
export const REFERRAL_REFERRALS = '/referrals/';
export const REFERRAL_INVITES = '/invites/';
export const REFERRAL_VALIDATE_CODE = '/codes/validate/';
export const REFERRAL_APPLY_CODE = '/codes/apply/';
export const REFERRAL_UPDATE_INVITE_STATUS = '/referrals/update-invite-status/';

// Admin User Management
export const ADMIN_USERS = '/accounts/admin/users/';
export const ADMIN_SET_PASSWORD = '/accounts/admin/users/set_password/';
export const DEMO_USER = '/accounts/admin/users/demo_user/';

// ============================================================================
// EXAMS & TESTING APIs
// ============================================================================
export const QUESTIONS = '/exams/questions/';
export const QUESTIONS_FILTER = '/exams/questions/filter/';
export const QUESTIONS_COUNT = '/exams/questions/count/';
export const QUESTIONS_UPLOAD = '/exams/questions/upload/';
export const QUESTIONS_UPLOAD_IMAGE = '/exams/questions/upload_image/';
export const QUESTIONS_AI_GENERATE = '/exams/questions/generate/';
export const QUESTIONS_AI_EXTRACT_VISUAL_CONTEXT = '/exams/questions/extract_visual_context/';
export const QUESTIONS_AI_GENERATE_VISUAL = '/exams/questions/generate_visual/';
export const QUESTIONS_BULK_CREATE = '/exams/questions/bulk_create/';
export const QUESTIONS_WITH_MISSING_ANSWERS = '/exams/questions/with_missing_answers/';
export const QUESTIONS_MISSING_ANSWERS_TOPIC_COUNTS = '/exams/questions/missing_answers_topic_counts/';
export const QUESTIONS_UPDATE_ANSWERS_WITH_AI = '/exams/questions/update_answers_with_ai/';
export const AI_FEEDBACK_SAVE = '/ai-feedback/save/';
export const AI_FEEDBACK_ENABLED = process.env.REACT_APP_AI_FEEDBACK_ENABLED !== 'false';
export const AI_FEEDBACK_UI_ENABLED = process.env.REACT_APP_AI_FEEDBACK_UI_ENABLED !== 'false';

export const TESTS = '/exams/tests/';
export const TESTS_GENERATE = '/exams/tests/generate/';
export const TESTS_QUESTIONS = '/exams/tests/';
export const TESTS_ADD_QUESTIONS = '/exams/tests/add_questions/';
export const TESTS_WORKSHEET = '/exams/tests/worksheet/';

export const ASSIGNMENTS = '/exams/assignments/';
/** Teacher offline / roster (pre-onboarding) attempt lifecycle */
export const ATTEMPT_START_MANAGED = '/exams/attempts/start-managed/';
export const attemptCompleteManagedUrl = (attemptId) => `/exams/attempts/${attemptId}/complete-managed/`;
/** Adaptive exam prep plans (list/create + nested sessions) */
export const EXAM_PREP_PLANS = '/exams/exam-prep/plans/';
export const ATTEMPTS = '/exams/attempts/';
export const ANSWERS = '/exams/answers/';
export const SUB_ANSWERS = '/exams/sub-answers/';
export const DETAIL_ANSWERS = '/exams/detail-answers/';
export const REVISIONS = '/exams/revisions/';
export const TEST_ATTEMPT_NOTE = '/exams/test-attempt-note/';

export const EXAM_MODES = '/exams/exam-modes/';
export const BOARDS_EXAMS = '/exams/boards/';

export const META_TAGS = '/exams/meta-tags/';

// Student-specific exam endpoints
export const STUDENT_AVAILABLE_TESTS = '/exams/student/available/';
export const STUDENT_START_TEST = '/exams/student/tests/';
export const STUDENT_ATTEMPTS = '/exams/attempts/';
// Fixed: Changed from '/exams/student/attempts/results/' to '/exams/student/attempts/'
export const STUDENT_RESULTS = '/exams/student/attempts/';
export const STUDENT_RESULTS_DETAIL = '/exams/student/attempts/';
export const STUDENT_ANALYTICS = '/exams/student/analytics/';
export const STUDENT_REWARDS_SUMMARY = '/exams/student/rewards/summary/';
export const STUDENT_ASSIGNMENTS = '/exams/student/assignments/';
export const STUDENT_TOPIC_PERFORMANCE = '/exams/results/topic-performance/';
export const STUDENT_TOPIC_PERFORMANCE_SUMMARY = '/exams/results/topic-performance-summary/';

// Analytics endpoints
export const ANALYTICS_STUDENT_PERFORMANCE = '/analytics/student/performance/';
export const ANALYTICS_TOPIC_PERFORMANCE = '/analytics/topic/performance/';
export const ANALYTICS_CONFIDENCE_ACCURACY = '/analytics/confidence/';
export const ANALYTICS_TEACHER_DASHBOARD = '/analytics/teacher/dashboard/';
export const ANALYTICS_STUDENT_DASHBOARD = '/analytics/student/';

// ============================================================================
// STRIPE PAYMENT APIs
// ============================================================================
export const STRIPE_CONFIG = '/stripe/config/';
export const STRIPE_CREATE_PAYMENT_INTENT = '/stripe/create-payment-intent/';
export const STRIPE_CREATE_SUBSCRIPTION = '/stripe/create-subscription/';
export const STRIPE_SUBSCRIPTION_STATUS = '/stripe/subscription-status/';
export const STRIPE_CANCEL_SUBSCRIPTION = '/stripe/cancel-subscription/';

// ============================================================================
// BILLING APIs (backend-driven; Stripe Checkout/Portal, plan changes)
// ============================================================================
export const BILLING_SUMMARY = '/billing/summary/';
export const BILLING_VALIDATE_PROMO = '/billing/validate-promo/';
export const BILLING_VALIDATE_PROMO_REGISTRATION = '/billing/validate-promo-registration/';
export const BILLING_UPGRADE_PREVIEW = '/billing/upgrade/preview/';
export const BILLING_UPGRADE_START = '/billing/upgrade/start/';
export const BILLING_CHECKOUT_SUBSCRIPTION = '/billing/checkout/subscription/';
export const BILLING_CHECKOUT_RETRY = '/billing/checkout/retry/';
export const BILLING_RECORD_CHECKOUT_CANCELED = '/billing/record-checkout-canceled/';
export const BILLING_CHECKOUT_ADDON = '/billing/checkout/addon/';
export const BILLING_PORTAL = '/billing/portal/';
export const BILLING_CHANGE_PLAN = '/billing/change-plan/';
export const BILLING_CANCEL = '/billing/cancel/';
export const BILLING_REACTIVATE = '/billing/reactivate/';

// ============================================================================
// SUPPORT & FEEDBACK APIs
// ============================================================================
export const SUPPORT_CREATE = '/support/';
export const SUPPORT_ALL = '/support/all/';

// ============================================================================
// THROTTLE & SYSTEM APIs
// ============================================================================
export const THROTTLE_CONFIGS = '/throttle/configs/';
export const THROTTLE_CHECK = '/throttle/check/';

// ============================================================================
// REFERRAL VALIDATION & USER CHECK APIs
// ============================================================================
export const REFERRAL_RECORD_INVITE_VISIT = (token) => `/referrals/record-invite-visit/${token}/`;
export const REFERRAL_VALIDATE_INVITE = '/referrals/validate-invite/';
export const CHECK_USER_EXISTS = '/accounts/check-user-exists/';
export const LEGAL_AVAILABLE = '/accounts/legal/available/';

// ============================================================================
// QUESTION REVIEW & APPROVAL APIs
// ============================================================================
export const QUESTION_REVIEWS = '/exams/questions/';
export const QUESTION_UPDATE_STATUS = '/exams/questions/';

// ============================================================================
// GOOGLE CALENDAR INTEGRATION APIs
// ============================================================================
export const GOOGLE_CALENDAR_AUTH = '/accounts/google-calendar/auth/';
export const GOOGLE_CALENDAR_CALLBACK = '/accounts/google-calendar/callback/';
export const GOOGLE_CALENDAR_SYNC = '/accounts/google-calendar/sync/';
export const GOOGLE_CALENDAR_DISCONNECT = '/accounts/google-calendar/disconnect/';
export const GOOGLE_CALENDAR_EVENTS = '/accounts/calendar/events/';

// ============================================================================
// NUDGES & MESSAGING APIs
// ============================================================================
export const NUDGES_BASE = '/nudges/';
export const NUDGES_UNREAD = '/nudges/unread/';
export const NUDGES_SENT = '/nudges/sent/';

// Nudge Configuration
export const NUDGE_AUTO_FETCH = true; // Auto-fetch on initial load

// ============================================================================
// AWARD & RECOGNITION APIs
// ============================================================================
export const AWARDS_AVAILABLE = '/exams/student/awards/assignments/available_awards/';
export const AWARDS_MY = '/exams/student/awards/my_awards/';
export const TEACHER_AWARDS = '/exams/teacher/awards/';
export const AWARDS_IMAGES = '/exams/awards/images/';
export const STUDENT_AWARDS_ASSIGNMENTS = '/exams/student/awards/assignments/';

// ============================================================================
// UPLOAD & FILE MANAGEMENT APIs
// ===========================================================================
export const UPLOAD_ENDPOINTS = {
  TEACHER: '/teacher-uploads/',
  STUDENT: '/student-uploads/',
  ADMIN: '/admin-uploads/'
};

export const PDF_ANNOTATIONS = '/pdf-annotations/';
export const AI_WORKSHEET_REVIEW_CONFIG = '/worksheets/ai-review/config/';
export const AI_WORKSHEET_REVIEW = (worksheetId) => `/worksheets/${worksheetId}/ai-review/`;
export const AI_WORKSHEET_REVIEW_LATEST = (worksheetId) => `/worksheets/${worksheetId}/ai-review/latest/`;

// Media proxy endpoints
export const MEDIA_PROXY = '/exams/media-proxy/0/image/';

// ============================================================================
// TEST TYPES CONSTANTS
// ============================================================================
export const TEST_TYPES = {
  QUIZ: 'QUIZ',
  ASSESSMENT: 'ASSESSMENT',
  PRE_ASSESSMENT: 'Pre-Assessment',
  SKILL_CHECK: 'Skill Check',
  REVISE: 'REVISE'
};

// Test type options for dropdowns (with display labels)
export const TEST_TYPE_OPTIONS = [
  { value: TEST_TYPES.QUIZ, label: 'Quiz' },
  { value: TEST_TYPES.ASSESSMENT, label: 'Assessment' },
  { value: TEST_TYPES.PRE_ASSESSMENT, label: 'Pre-Assessment' },
  { value: TEST_TYPES.SKILL_CHECK, label: 'Skill Check' },
  { value: TEST_TYPES.REVISE, label: 'Revision Test' }
];
